// @ts-strict-ignore
import {h} from 'panel';

export interface FormatTextOptions {
  /** optional classname for the returned root div */
  className?: string;
  /** add ellipsis descriptions longer than maxLength. Default is no truncation */
  maxLength?: number;
  /** supports multiline \n characters */
  multiline?: boolean;
}

interface FormatElementBase<T> {
  type: T;
  text: string;
}

type FormatPartBr = FormatElementBase<`br`>;
type FormatPartSpan = FormatElementBase<`span`>;
interface FormatPartAnchor extends FormatElementBase<`a`> {
  href: string;
  target?: string;
}

type FormatPart = FormatPartSpan | FormatPartAnchor | FormatPartBr;

/**
 * Returns a snabbdom vnode with markdown like text formatted into spans and links
 */
export function formatText(
  text: string,
  {className = ``, maxLength = 1000, multiline = false}: FormatTextOptions = {},
  inlineElements = [],
) {
  // This regex matches line breaks, naked links and markdown-formatted links
  // e.g [google](https://google.com) and https://google.com and mailto:email@mixpanel.com
  const formatRegex =
    /\[([^\]]+)\]\(((https?:\/\/\S+)|(mailto:\S+)|(\/\S*))\)(?:{:target="([a-z_]+)"})?|(https?:\/\/\S+)|(\n)/gm;
  // parse text into span / anchor parts
  const parseParts: FormatPart[] = [];
  let lastIndex = 0;
  let formatMatch: RegExpExecArray;
  do {
    formatMatch = formatRegex.exec(text);
    if (formatMatch) {
      // add text upto link
      parseParts.push({type: `span`, text: text.slice(lastIndex, formatMatch.index)});
      // new line
      if (multiline && formatMatch[8]) {
        parseParts.push({type: `br`, text: ``});
      } else {
        const [_, mdLinkText, mdLinkHref] = formatMatch;
        const nakedLinkHref = formatMatch[7];
        if (nakedLinkHref) {
          parseParts.push({type: `a`, href: nakedLinkHref, text: nakedLinkHref, target: `_blank`});
        } else if (mdLinkText) {
          parseParts.push({type: `a`, href: mdLinkHref, text: mdLinkText, target: formatMatch[6] || `_blank`});
        }
      }
      lastIndex = formatMatch.index + formatMatch[0].length;
    }
  } while (formatMatch !== null);
  // add remaining text
  parseParts.push({type: `span`, text: text.slice(lastIndex)});

  // generate the vdom children and truncate text if necessary
  const hParts = [];
  let lastLength = 0;
  for (const part of parseParts) {
    if (part.type === `br`) {
      hParts.push(h(part.type, {}));
    } else if (lastLength < maxLength && part.text) {
      const partLength = part.text.length;
      // prettier-ignore
      const partText = (partLength + lastLength) <= maxLength
        ? part.text
        : part.text.substr(0, maxLength - lastLength) + `...`;
      lastLength += partLength;

      if (part.type === `a`) {
        hParts.push(h(part.type, {attrs: {href: part.href, target: part.target}}, partText));
      } else {
        hParts.push(h(part.type, {}, partText));
      }
    }
  }

  for (const inlineElement of inlineElements) {
    hParts.push(inlineElement);
  }
  return h(`div`, className ? {class: {[className]: !!className}} : {}, hParts);
}
